import React, { createContext, useContext, useState, useEffect } from "react";
import { professionalTimeline, academicTimeline, communityTimeline, socialLinks } from "./config";
import { curatedList } from "./config";

interface DynamicContent {
  settings: any[];
  timeline: any[];
  hobbies: any[];
  causes: any[];
  blogs: any[];
  socials: any[];
  categories: any[];
  tags: any[];
  projects: any[];
  faqs: any[];
  testimonials: any[];
  loading: boolean;
  refresh: () => Promise<void>;
}

const DynamicContentContext = createContext<DynamicContent | undefined>(undefined);

export const DynamicContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<Omit<DynamicContent, "loading" | "refresh">>({
    settings: [],
    timeline: [],
    hobbies: [],
    causes: [],
    blogs: [],
    socials: [],
    categories: [],
    tags: [],
    projects: [],
    faqs: [],
    testimonials: []
  });
  const [loading, setLoading] = useState(true);

  const fetchContent = async () => {
    try {
      const response = await fetch("/api/content");
      const result = (await response.json()) as any;
      if (result.success) {
        setData({
          settings: result.settings || [],
          timeline: result.timeline || [],
          hobbies: result.hobbies || [],
          causes: result.causes || [],
          blogs: result.blogs || [],
          socials: result.socials || [],
          categories: result.categories || [],
          tags: result.tags || [],
          projects: result.projects || [],
          faqs: result.faqs || [],
          testimonials: result.testimonials || []
        });
      }
    } catch (err) {
      console.warn("Dynamic API fetch failed, falling back to static config.", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  return (
    <DynamicContentContext.Provider value={{ ...data, loading, refresh: fetchContent }}>
      {children}
    </DynamicContentContext.Provider>
  );
};

export const useDynamicContent = () => {
  const context = useContext(DynamicContentContext);
  if (!context) {
    throw new Error("useDynamicContent must be used within a DynamicContentProvider");
  }
  return context;
};

// Fallback resolver functions
function parseDetails(details: any): any[] {
  if (details === undefined || details === null) {
    return [];
  }
  if (typeof details === "string") {
    try {
      const parsed = JSON.parse(details || "[]");
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [details];
    }
  }
  return Array.isArray(details) ? details : [details];
}

export function parseLinks(links: any): any[] {
  if (links === undefined || links === null) {
    return [];
  }
  if (typeof links === "string") {
    try {
      const parsed = JSON.parse(links || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(links) ? links : [];
}

function parseTimelineEvent(e: any): any {
  const parsedEn = parseDetails(e.details_en);
  const parsedTr = parseDetails(e.details_tr);
  const durationEn = e.duration_en || e.duration || "";
  const durationTr = e.duration_tr || e.duration || "";
  return {
    id: e.id,
    year: e.year,
    company: { en: e.company_en, tr: e.company_tr },
    role: { en: e.role_en, tr: e.role_tr },
    duration: { en: durationEn, tr: durationTr },
    logo_url: e.logo_url || "",
    details: {
      en: parsedEn,
      tr: parsedTr
    },
    links: parseLinks(e.links)
  };
}

export function getTimelineEvents(type: string, dynamicList: any[], staticList: any[]): any[] {
  const dynamicFiltered = dynamicList.filter((e) => e.type === type);
  if (dynamicFiltered.length > 0) {
    return dynamicFiltered
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map(parseTimelineEvent);
  }
  return staticList;
}

export function getCuratedHobbies(dynamicHobbies: any[]): any[] {
  if (dynamicHobbies.length > 0) {
    return dynamicHobbies.map((h) => ({
      id: h.id,
      title: { en: h.title_en, tr: h.title_tr },
      subtitle: { en: h.subtitle_en, tr: h.subtitle_tr },
      category: h.category,
      link: h.link || ""
    }));
  }
  return curatedList;
}

export function getSocialLinks(dynamicSocials: any[]): any[] {
  if (dynamicSocials.length > 0) {
    return dynamicSocials.map((s) => ({
      name: s.name,
      url: s.url,
      icon: s.icon,
      color: s.color || "hover:text-brand-primary"
    }));
  }
  return socialLinks;
}

export interface Testimonial {
  id: string;
  author: { en: string; tr: string };
  role: { en: string; tr: string };
  company: { en: string; tr: string };
  quote: { en: string; tr: string };
  avatar_url: string;
  sort_order: number;
  links?: { name_en: string; name_tr: string; url: string }[];
}

export function getCuratedTestimonials(dynamicTestimonials: any[]): Testimonial[] {
  if (dynamicTestimonials && dynamicTestimonials.length > 0) {
    return dynamicTestimonials.map((t) => ({
      id: t.id,
      author: { en: t.author_en, tr: t.author_tr },
      role: { en: t.role_en, tr: t.role_tr },
      company: { en: t.company_en, tr: t.company_tr },
      quote: { en: t.quote_en, tr: t.quote_tr },
      avatar_url: t.avatar_url || "",
      sort_order: t.sort_order || 0,
      links: parseLinks(t.links)
    }));
  }
  return [];
}
